import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Share, SquarePlus, MoreVertical, Smartphone, Monitor, Download, CheckCircle2, Compass } from "lucide-react";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";

const detect = () => {
  const ua = navigator.userAgent;
  if (/iphone|ipad|ipod/i.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)) return "ios";
  if (/android/i.test(ua)) return "android";
  return "desktop";
};
const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;

const Step = ({ n, icon: Icon, children }) => (
  <li className="flex gap-4" data-testid={`app-step-${n}`}>
    <span className="w-8 h-8 rounded-full border border-primary/50 text-primary flex items-center justify-center text-sm shrink-0">{n}</span>
    <p className="text-sm text-muted-foreground leading-relaxed pt-1">
      {Icon && <Icon size={14} className="inline -mt-0.5 mr-1.5 text-foreground" />}
      {children}
    </p>
  </li>
);

const IosSteps = () => (
  <ol className="space-y-5">
    <Step n={1} icon={Compass}>Open <strong className="text-foreground">our-ripple-effect-community.com</strong> in <strong className="text-foreground">Safari</strong>. If you tapped the link inside Instagram, Facebook, or Gmail, tap the <strong className="text-foreground">•••</strong> menu and choose <strong className="text-foreground">Open in Safari</strong> first — the in-app browsers hide the option.</Step>
    <Step n={2} icon={Share}>Tap the <strong className="text-foreground">Share</strong> button — the square with an arrow pointing up, at the bottom of the screen (top right on iPad).</Step>
    <Step n={3} icon={SquarePlus}>Scroll down the list and tap <strong className="text-foreground">Add to Home Screen</strong>.</Step>
    <Step n={4} icon={CheckCircle2}>Tap <strong className="text-foreground">Add</strong>. O.R.E.C now lives on your home screen and opens full-screen, just like an app.</Step>
  </ol>
);

const AndroidSteps = ({ prompt, onInstall }) => (
  <div className="space-y-5">
    {prompt && (
      <Button onClick={onInstall} className="rounded-full px-6" data-testid="app-install-btn">
        <Download size={14} className="mr-2" /> Install O.R.E.C
      </Button>
    )}
    <ol className="space-y-5">
      <Step n={1} icon={Compass}>Open the site in <strong className="text-foreground">Chrome</strong> (or Samsung Internet).</Step>
      <Step n={2} icon={MoreVertical}>Tap the <strong className="text-foreground">⋮</strong> menu in the top right corner.</Step>
      <Step n={3} icon={SquarePlus}>Tap <strong className="text-foreground">Install app</strong> or <strong className="text-foreground">Add to Home screen</strong>, then confirm.</Step>
    </ol>
  </div>
);

const DesktopSteps = ({ prompt, onInstall }) => (
  <div className="space-y-5">
    {prompt && (
      <Button onClick={onInstall} className="rounded-full px-6" data-testid="app-install-btn">
        <Download size={14} className="mr-2" /> Install O.R.E.C
      </Button>
    )}
    <ol className="space-y-5">
      <Step n={1} icon={Monitor}>In <strong className="text-foreground">Chrome</strong> or <strong className="text-foreground">Edge</strong>, look for the small <strong className="text-foreground">install icon</strong> at the right end of the address bar.</Step>
      <Step n={2} icon={SquarePlus}>Click it and choose <strong className="text-foreground">Install</strong> — or use the ⋮ menu → <strong className="text-foreground">Install O.R.E.C</strong>.</Step>
    </ol>
  </div>
);

const PLATFORMS = [
  { key: "ios", label: "iPhone & iPad", icon: Smartphone },
  { key: "android", label: "Android", icon: Smartphone },
  { key: "desktop", label: "Laptop & desktop", icon: Monitor },
];

export default function GetTheApp() {
  const [platform, setPlatform] = useState("ios");
  const [installed, setInstalled] = useState(false);
  const [prompt, setPrompt] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    setPlatform(detect());
    setInstalled(isStandalone());
    setPrompt(window.__orecInstallPrompt || null);
    const ready = () => setPrompt(window.__orecInstallPrompt || null);
    window.addEventListener("orec-install-ready", ready);
    return () => window.removeEventListener("orec-install-ready", ready);
  }, []);

  const install = async () => {
    if (!prompt) return;
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome === "accepted") setInstalled(true);
    window.__orecInstallPrompt = null;
    setPrompt(null);
  };

  return (
    <div className="silk-hero min-h-[70vh] px-5 sm:px-8 py-16">
      <SEO title="Get the app — O.R.E.C" description="Add O.R.E.C to your phone's home screen in a few taps — no app store, no download." />
      <div className="max-w-2xl mx-auto" data-testid="get-the-app-page">
        <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-3">Get the app</p>
        <h1 className="font-display text-4xl sm:text-5xl text-foreground mb-3">O.R.E.C on your home screen</h1>
        <p className="text-sm text-muted-foreground mb-10 leading-relaxed">
          O.R.E.C works as an app straight from your browser — nothing to download from an app store. Add it once and it opens full-screen with its own icon, always signed in.
        </p>

        {installed && (
          <div className="silk-card rounded-lg p-5 mb-8 flex items-center gap-3" data-testid="app-already-installed">
            <CheckCircle2 size={18} className="text-primary shrink-0" />
            <p className="text-sm text-foreground">You're already using O.R.E.C as an app. ✦</p>
          </div>
        )}

        <div className="flex flex-wrap gap-2 mb-8" data-testid="app-platform-tabs">
          {PLATFORMS.map((p) => (
            <button key={p.key} onClick={() => setPlatform(p.key)} data-testid={`app-platform-${p.key}`}
              className={`rounded-full border px-4 py-1.5 text-xs uppercase tracking-wider transition-colors ${platform === p.key ? "border-primary text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
              <p.icon size={12} className="inline -mt-0.5 mr-1.5" />{p.label}
            </button>
          ))}
        </div>

        <section className="silk-card rounded-lg p-6 sm:p-8" data-testid={`app-steps-${platform}`}>
          <h2 className="font-display text-base md:text-lg text-foreground mb-6">{PLATFORMS.find((p) => p.key === platform).label}</h2>
          {platform === "ios" && <IosSteps />}
          {platform === "android" && <AndroidSteps prompt={prompt} onInstall={install} />}
          {platform === "desktop" && <DesktopSteps prompt={prompt} onInstall={install} />}
        </section>

        <p className="text-xs text-muted-foreground mt-10 leading-relaxed">
          Stuck? <Link to="/contact" className="text-primary hover:underline">Send the Team a message</Link> and we'll walk you through it.
        </p>
      </div>
    </div>
  );
}
